import { Link } from 'react-router-dom'
import './Home.css'

const PILLARS_EN = [
  { label: 'Technology & AI', to: '/technology' },
  { label: 'Digital platforms', to: '/technology#platforms' },
  { label: 'Energy', to: '/technology#energy' },
  { label: 'Logistics', to: '/transport' },
]

const PILLARS_FR = [
  { label: 'Technologie et IA', to: '/technology' },
  { label: 'Plateformes digitales', to: '/technology#platforms' },
  { label: 'Énergie', to: '/technology#energy' },
  { label: 'Logistique', to: '/transport' },
]

const PRODUCTS_EN = [
  {
    icon: '📚',
    tag: 'EdTech · LMS bilingue',
    name: 'OnboardLearning.org',
    body: 'AI-enabled bilingual learning platform for education and skills — built for learners and institutions.',
    href: 'https://onboardlearning.org',
  },
  {
    icon: '🛒',
    tag: 'Marketplace · Bangui',
    name: 'Galababa.com',
    body: 'Local digital commerce marketplace connecting sellers and buyers in Bangui — designed for on-the-ground realities.',
    href: 'https://galababa.com',
  },
  {
    icon: '💬',
    tag: 'Live chat · Domains',
    name: 'KesseDesk',
    body: 'KesseDesk — live chat and domains for businesses: add customer support to your website in minutes.',
    href: 'https://www.kessedesk.com',
  },
]

const PRODUCTS_FR = [
  {
    icon: '📚',
    tag: 'EdTech · LMS bilingue',
    name: 'OnboardLearning.org',
    body: 'Plateforme d\'apprentissage bilingue assistée par l\'IA — conçue pour les apprenants et les institutions.',
    href: 'https://onboardlearning.org',
  },
  {
    icon: '🛒',
    tag: 'Marketplace · Bangui',
    name: 'Galababa.com',
    body: 'Marketplace de commerce digital local à Bangui — connecte vendeurs et acheteurs, adaptée au terrain.',
    href: 'https://galababa.com',
  },
  {
    icon: '💬',
    tag: 'Chat en direct · Domaines',
    name: 'KesseDesk',
    body: 'KesseDesk — chat en direct et noms de domaine pour les entreprises : ajoutez le support client à votre site en quelques minutes.',
    href: 'https://www.kessedesk.com',
  },
]

function HomePage({ t }) {
  const fr = /digitalisation|Technologie et/i.test(t.hero?.eyebrow || '') || /Holding Technologie/i.test(t.tagline || '')
  const pillars = fr ? PILLARS_FR : PILLARS_EN
  const products = fr ? PRODUCTS_FR : PRODUCTS_EN

  return (
    <div className="home-page">
      {/* Full-width navy hero — tech headline, no trucks */}
      <section id="home" className="home-hero">
        <div className="home-hero-inner">
          <span className="eyebrow">{t.hero.eyebrow}</span>
          <h1>{t.hero.title}</h1>
          <p className="home-hero-sub">{t.hero.subtitle}</p>
          <div className="hero-actions">
            <Link className="btn primary" to="/technology">
              {fr ? 'Découvrir la technologie' : 'Explore Technology'}
            </Link>
            <Link className="btn secondary" to="/contact">
              {t.hero.primary}
            </Link>
          </div>
          <div className="home-hero-stats">
            {t.stats.map((stat) => (
              <div key={stat.label}>
                {stat.value}
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar order strip */}
      <div className="pillar-strip">
        <div className="pillar-strip-inner">
          {pillars.map((p, i) => (
            <span key={p.to} style={{ display: 'contents' }}>
              {i > 0 && <span className="pillar-arrow" aria-hidden>→</span>}
              <Link className="pillar-chip" to={p.to}>
                <span className="pillar-chip-num">{i + 1}</span>
                {p.label}
              </Link>
            </span>
          ))}
        </div>
      </div>

      {/* Who we are */}
      <section id="about" className="home-section">
        <div className="home-section-header about-teaser">
          <h2>{t.about.title}</h2>
          <p>{t.about.lead}</p>
          {t.about.paragraphs?.[0] && <p>{t.about.paragraphs[0]}</p>}
          <Link className="btn primary" to="/about">
            {t.about.readMore}
          </Link>
        </div>
      </section>

      {/* Flagship products */}
      <section className="home-section alt" id="products">
        <div className="home-section-inner">
          <div className="home-section-header">
            <h2>{fr ? 'Nos plateformes' : 'Our Platforms'}</h2>
            <p>
              {fr
                ? 'Produits phares — éducation, commerce digital et support client.'
                : 'Flagship products — education, digital commerce and customer support.'}
            </p>
          </div>
          <div className="product-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {products.map((p) => (
              <a
                key={p.href}
                className="product-card"
                href={p.href}
                target="_blank"
                rel="noreferrer"
              >
                <span className="product-card-icon">{p.icon}</span>
                <span className="product-card-tag">{p.tag}</span>
                <h3>{p.name}</h3>
                <p>{p.body}</p>
                <span className="product-card-link">
                  {fr ? 'Visiter →' : 'Visit →'}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Business pillars */}
      <section id="businesses" className="home-section">
        <div className="home-section-header">
          <h2>{t.businesses.title}</h2>
          <p>{t.businesses.intro}</p>
        </div>
        <div className="businesses-grid">
          <div className="cards">
            {t.businesses.items.map((item) => {
              const icon = (
                <span className="business-icon" aria-label={item.iconAlt}>
                  {item.iconSvg
                    ? item.iconSvg
                    : item.iconEmoji
                      ? <span className="business-icon-emoji">{item.iconEmoji}</span>
                      : item.icon
                        ? <img className="business-icon-image" src={item.icon} alt={item.iconAlt} />
                        : null}
                </span>
              )
              const body = (
                <>
                  {icon}
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  {item.products && (
                    <ul className="business-products">
                      {item.products.map((pr) => (
                        <li key={pr.href}>
                          <a href={pr.href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                            {pr.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                  <span className="business-link">{t.businesses.readMore}</span>
                </>
              )
              if (item.external) {
                return (
                  <a className="card business-card" key={item.title} href={item.external} target="_blank" rel="noreferrer">
                    {body}
                  </a>
                )
              }
              return (
                <Link className="card business-card" key={item.title} to={item.route}>
                  {body}
                </Link>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
